// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import { i18next } from '@hotjar/translations';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import { getTrialInfo } from '@hotjar/webapp/domains/activation/trials/get-trial-info';
import { mapSiteAccountDtoToModel, mapOwnedAccountDtoToModel, isAccountExpired } from '@hotjar/account';

/**
 * Account service which includes actions related to an Account.
 * NOTE: An account is NOT a user but a Hotjar account.
 */
angular.module('hjApp.user').service('Account', [
    '$filter',
    '$q',
    '$rootScope',
    'Notify',
    'Restangular',
    'restDefaultHandlers',
    'User',
    'UserData',
    function ($filter, $q, $rootScope, Notify, Restangular, restDefaultHandlers, User, UserData) {
        /********************
         * PRIVATE VARIABLES
         ********************/
        var self = this;

        /********************
         * PUBLIC ACCESS FUNCTIONS
         ********************/

        /**
         * Get an account based on it's ID.
         * NOTE: This will only return an account if the user is an account owner.
         *
         * @param accountId ID of account to get data for.
         */
        this.get = function (accountId) {
            var account = _.find(UserData.get('accounts'), { id: accountId });

            return account || null;
        };

        /**
         * Get the user's current account.
         * NOTE: This is currently the first one in their accounts list.
         * NOTE: This will only return an account if the user is an account owner.
         *
         * @returns {account/null} Their current account or null.
         */
        this.getOwned = function () {
            return UserData.get('accounts') ? UserData.get('accounts')[0] : null;
        };

        /**
         * Returns true if the user is the owner of a specific account.
         *
         * @param account Account to check.
         * @returns {boolean} True if user is account owner, false otherwise.
         */
        this.isOwner = function (account) {
            var isOwner;

            // Do not proceed if we have no account.
            if (_.isUndefined(account)) {
                throw new Error('Account.isOwner(): You must specify (pass) an account to use this function.');
            }

            // If we have an account object for this account ID,
            // it means the user is an owner.
            isOwner = self.get(account.id);

            return Boolean(isOwner);
        };

        /**
         * Set property/ies related to an account and broadcast an event.
         *
         * @param accountId ID of account to update.
         * @param nameOrMap Key for item to set OR a map of key value pairs.
         * @param value Value to set OR undefined
         */
        this.set = function (accountId, nameOrMap, value) {
            var accountIndex = _.findIndex(UserData.get('accounts'), { id: accountId }),
                accountObject = UserData.accounts[accountIndex];

            // If we don't find the accont object, don't continue.
            if (accountIndex < 0) {
                throw new Error('Account.set(): Account not found.');
            }

            // Update the UserData accounts object.
            // ToDo: Can this somehow be part of UserData.set() instead?
            //       The issue is that the accounts property is an Array and simply using _.extend
            //       will replace the whole thing, rather than updating just the properties we pass it.
            if (_.isObject(nameOrMap)) {
                _.extend(accountObject, nameOrMap, function (objValue, srcValue) {
                    return srcValue;
                });
            } else {
                accountObject[nameOrMap] = value;
            }

            // Save UserData.
            UserData.save();

            // Broadcast an event and attach the updated object to it.
            // We intentionally do not return the object and rely on $on.
            // This needs to be updated once UserData is cleaned up.
            queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY);
            $rootScope.$broadcast('accountsUpdated');
        };

        /**
         * Returns information regarding an Account's trial.
         *
         * @param account Account to return trial information for.
         * @returns {object} Object containing trial information.
         */
        this.getTrialInfo = function (account) {
            // We pass account twice here since `account` here is actually a merged object with the
            // `SiteAccount` and `OwnedAccount` objects properties in it.
            return getTrialInfo(mapSiteAccountDtoToModel(account), mapOwnedAccountDtoToModel(account));
        };

        /**
         * Number of days to the next billing date. Defaults to null if null or undefined.
         *
         * @param account Account to check for.
         * @returns {number} of days.
         */
        this.daysUntilNextBillingDate = function (account) {
            return account.next_billing_date ? $filter('daysFromNow')(account.next_billing_date) : null;
        };

        /**
         * Returns true if the account's has no payment details despite being customer and next_billing_date is past.
         *
         * @param account
         * @returns {boolean}
         */
        this.paymentExpired = function (account) {
            return isAccountExpired(mapOwnedAccountDtoToModel(account));
        };

        /**
         * Get the sites owned by an account and return a Promise.
         *
         * @param {object} account The Account to filter
         * @param {string} sortBy The field to sort by
         * @param {Function} extraFilter Function to run on the output.
         *
         * @returns {Promise.<Object[]>} Promise to resolve once we have the sites.
         */
        this.getSites = function (account, sortBy, extraFilter) {
            var accountId = account && account.id;

            // Do not proceed if we have no account.
            if (_.isUndefined(accountId)) {
                throw new Error('Account.getSites(): You must specify (pass) an account to use this function.');
            }
            return UserData.getSites().then(function (sites) {
                sortBy = sortBy || 'id';
                extraFilter = _.isFunction(extraFilter) ? extraFilter : _.wrap(true);

                return _(sites)
                    .filter(function (site) {
                        return site.account.id === accountId && extraFilter(site);
                    })
                    .unique(function (site) {
                        return site.id;
                    })
                    .sortBy(sortBy)
                    .value();
            });
        };

        /**
         * Get the organizations owned by an account and return a Promise.
         *
         * @param {*} account Account to get organizations for.
         * @param {string} sortBy Sorting key.
         *
         * @returns {Promise.<Object[]>} Promise to resolve once we have the organizations.
         */
        this.getOrganizations = function (account, sortBy) {
            var accountId = account && account.id;

            // Do not proceed if we have no account.
            if (_.isUndefined(account)) {
                throw new Error('Account.getOrganizations(): You must specify (pass) an account to use this function.');
            }
            sortBy = sortBy || 'id'; // Default: Organization ID.

            return UserData.getOrganizations().then(function (organizations) {
                return _(organizations).filter({ account_id: accountId }).unique('id').sortBy(sortBy).value();
            });
        };

        /**
         * Get all the users which have access to any Organization owned by an account.
         *
         * @param {*} account Account to get users for.
         *
         * @returns {Promise.<Object[]>} Promise to resolve once we have the users.
         */
        this.getUsers = function (account) {
            var users = [];

            // Do not proceed if we have no account.
            if (_.isUndefined(account)) {
                throw new Error('Account.getUsers(): You must specify (pass) an account to use this function.');
            }

            return self.getOrganizations(account).then(function (organizations) {
                _.each(organizations, function (organization) {
                    users = users.concat(organization.users);
                });

                return _.uniq(users, 'email');
            });
        };

        /**
         * Filter a list of organizations to only return the ones owned by an account.
         *
         * @param {array} organizations The list of organizations to filter.
         * @param {object} account The Account to filter by.
         * @param {string} sortBy The field to sort by.
         * @param {Function} extraFilter Function to run on the output.
         *
         * @returns {array} Sites which belong to the given account.
         */
        this.filterOrganizationsByAccount = function filterOrganizationsByAccount(
            organizations,
            account,
            sortBy,
            extraFilter
        ) {
            sortBy = sortBy || 'id';
            extraFilter = _.isFunction(extraFilter)
                ? extraFilter
                : function () {
                      return true;
                  };

            return _(organizations)
                .filter(function (organization) {
                    return organization.account_id === account.id && extraFilter(organization);
                })
                .unique('id')
                .sortBy(sortBy)
                .value();
        };

        /**
         * Create a new account – used primarily through the site-create controller.
         * Note: An account can only be created with an Organization and site.
         *
         * @param accountId The referrer's account id.
         * @param ownerEmail Email address of new owner.
         * @param organizationName Name to use for new Organization.
         * @param siteUrl Url to use for new site.
         * @param posCallback function to run on success.
         */
        this.create = function (accountId, ownerEmail, organizationName, siteUrl, posCallback) {
            var deferred = $q.defer(),
                rOrganizations = Restangular.one('accounts', accountId).all('organizations');

            // Do not proceed if we don't have all the details.
            if (_.isUndefined(ownerEmail) || _.isUndefined(organizationName) || _.isUndefined(siteUrl)) {
                throw new Error('Account.create(): You must specify (pass) an email, organization name and site Url.');
            }

            // The plan is always set to Basic – handled through the API.
            rOrganizations
                .customPOST({
                    action: 'add_account_organization_site_and_user',
                    email: ownerEmail,
                    name: organizationName,
                    url: siteUrl
                })
                .then(
                    function (res) {
                        if (res.success) {
                            Notify.success('Organization and site added.');
                            UserData.refreshFromServer(function () {
                                $rootScope.$broadcast('siteCreated');
                                deferred.resolve();

                                if (_.isFunction(posCallback)) {
                                    posCallback(res);
                                }
                            });
                        } else {
                            Notify.error('Failed to add organization and site.');
                            deferred.reject();
                        }
                    },
                    function (res) {
                        Notify.error('Failed to add organization and site.');
                        deferred.reject();
                        restDefaultHandlers.onError(res);
                    }
                );

            return deferred.promise;
        };

        /**
         * Request access to an account by sending an email – used primarily through the site-create controller.
         *
         * @param ownerEmail Email address of new owner.
         * @param posCallback function to run on success.
         */
        this.sendEmailToRequestAccess = function (ownerEmail, posCallback) {
            var deferred = $q.defer(),
                user = User.get(),
                rUserOrganizations = Restangular.one('users', user.id).all('organizations');

            // Do not proceed if we don't have all the details.
            if (_.isUndefined(ownerEmail)) {
                throw new Error('Account.requestAccess(): You must specify (pass) an email.');
            }

            rUserOrganizations
                .customPOST({
                    action: 'request_invitation',
                    email: ownerEmail,
                    locale: i18next.resolvedLanguage ?? undefined
                })
                .then(
                    function (res) {
                        if (res.success) {
                            Notify.success('Request sent.');
                            deferred.resolve();
                            if (_.isFunction(posCallback)) {
                                posCallback(res);
                            }
                        } else {
                            Notify.error('Failed to request invitation.');
                            deferred.reject();
                        }
                    },
                    function (res) {
                        Notify.error('Failed to add organization and site.');
                        deferred.reject();
                        restDefaultHandlers.onError(res);
                    }
                );

            return deferred.promise;
        };
    }
]);
